import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "w-100 d-flex justify-content-start" }
const _hoisted_2 = { class: "datepicker-label" }
const _hoisted_3 = { class: "input-title" }
const _hoisted_4 = { class: "d-flex justify-content-around align-items-center m-2 border-top" }
const _hoisted_5 = { class: "input-title fw-bold m-2" }

import { computed, ComputedRef, onMounted, ref, watch } from "vue";
import { DatePicker as VDatePicker } from "v-calendar";
import {
parseDateString,
formatDateToString,
formatDateRange,
getDiffrenceDatesInNight,
} from "@/vue/planning/utils/datePickerHelpers";
import { AttributesRef, DateInfo, RangeType } from "@/types/datePicker";
import {
  getLanguageCode,
  getAdjacentMonth,
  parseHashParams,
  getSasonBasedOnDate,
  formatDateForHash,
} from "@/utils/utils";
import { HashParams } from "@/types";
import * as _ from "lodash";

import { onClickOutside } from "@vueuse/core";
// import store from '@/vue/planning/store/store'
import { useStore } from 'vuex'
import { format } from 'date-fns'

// Dynamic Refs - Dates Handler

export default /*@__PURE__*/_defineComponent({
  __name: 'DatePickerV2',
  setup(__props) {

// Imports
const store = useStore();
const content = ref<any>(store.state.content);
const startDate = ref<Date | null>(
parseDateString(store.state.dates["BeginDate"])
);
const endDate = ref<Date | null>(parseDateString(store.state.dates["EndDate"]));
const availableDates = ref<Date[]>([]);
const selectedDate = ref<Date | null>(startDate.value);
const prevSelectedDate = ref<Date | null>(null);
const currentMonth = ref<string>("");
const currentYear = ref<string>("");
const range = ref<RangeType>({ start: "", end: "" });

const attributesRef = ref<AttributesRef[]>([
  {
    key: "selected-dates",
    highlight: 'blue',
    dates: { start: new Date(), end: new Date() },
  },
]);


// Dynamic Refs - Elements Handler
const datePickerContainerRef = ref<HTMLElement | null>(null);
const datePickerRef = ref<InstanceType<typeof VDatePicker> | null>(null);
const isOpenedRef = ref<boolean>(false);
const selectedColor = ref("blue");

// Dynamic Refs - Miscellaneous
const isSelectingEndDateMode = ref<boolean>(false);
const generalParams = ref({
locale: store.getters.locale,
packCode: "",
liheId: window.lihe.id,
});

const availabilitiesParams = ref({
season: "",
});

const startDatesParams = ref({
month: "",
});

const locale = ref(getLanguageCode(store.getters.locale));

//Computed
const departureText = computed((): string => {
if (!startDate.value) return "";
return formatDateRange(store.getters.locale, startDate.value);
});

const returnText = computed((): string => {
if (!endDate.value) return "";
return `${formatDateRange(store.getters.locale, endDate.value)} (${getDiffrenceDatesInNight(range.value)} ${content.value.nights})`;
});

// Methods
const handleSearch = (startDate: any, endDate: any) => {
startDate.value = formatDateToString(startDate);
endDate.value = formatDateToString(endDate);

const datesObj = {
  BeginDate: startDate.value,
  EndDate: endDate.value,
};

store.dispatch("setDates", datesObj);
isSelectingEndDateMode.value = false;

toggleShowHideDatePicker(false);

// Update the range ref
range.value.start = startDate.value;
range.value.end = endDate.value;

// Update the attributesRef
attributesRef.value[0].dates = {
  start: parseDateString(startDate.value),
  end: parseDateString(endDate.value),
};

// Display again the available start dates for the months in display
const currentMonth = startDate.value.split("/")[1];
fetchStartDates(currentMonth, true);
};

const selectDate = async (date: DateInfo): Promise<void> => {
  if (date.isDisabled) return;

  // Format the date to match the expected format for getSasonBasedOnDate
  const formattedDate = formatDateForHash(date.id);
  
  // Check if we need to switch season based on selected date
  const seasonBasedOnDate = getSasonBasedOnDate(formattedDate);

  if (seasonBasedOnDate !== store.state.season) {
    store.commit("setSeason", seasonBasedOnDate);
    window.seasonManager.switchSeasonWithoutReloading();
    
    // Update params with new season
    availabilitiesParams.value.season = seasonBasedOnDate;
  }

  if (isSelectingEndDateMode.value) {
    handleSearch(prevSelectedDate.value, selectedDate.value);
    return;
  }

  if(!selectedDate.value) return;
  
  // First click in a valid date will turn isSelectingEndDateMode into 'true'
  isSelectingEndDateMode.value = true;

  // Update selectedDate
  prevSelectedDate.value = _.cloneDeep(selectedDate.value);
  selectedDate.value = parseDateString(date.id);

  // Fetch endDates
  startDatesParams.value.month = date.id.split("-").slice(0, 2).join("-");
  try {
    const res = await window.searchEngineService.getRelatedPackages(
      {
        ...generalParams.value,
        ...startDatesParams.value,
        startDate: date.id,
      },
      "getEndDates"
    );

    // Clean attributesRef and the availableDates section, and add the 'res' to availableDates it
    (attributesRef.value[0].dates = null), (availableDates.value = formatDatesArray(res));
  } catch (error) {
    console.error("Error fetching end dates at selectDate:", error);
  }
};


const toggleShowHideDatePicker = (show?: boolean) => {
const pickerDiv = document.getElementById(
  "datePickerDiv"
) as HTMLElement | null;

if (!pickerDiv || !datePickerContainerRef.value) return;

if (typeof show == "undefined") show = !isOpenedRef.value;

if (show) {
  setTimeout(() => {
    document.addEventListener("click", unfocuseDatePicker);
    pickerDiv.style.color = "var(--primary-blue)";
  }, 1);
} else {
  document.removeEventListener("click", unfocuseDatePicker);
  pickerDiv.style.color = "black";
}

isOpenedRef.value = show;
};

const unfocuseDatePicker = (event: MouseEvent) => {
if (
  event.target &&
  !(event.target as Element).closest(".datePickerContainerRef") &&
  !(event.target as Element).closest(".showDatePickerBtn")
) {
  toggleShowHideDatePicker(false);
}
};

const moveDatePickerMonth = async (month: string, year: string) => {
await datePickerRef.value.move({
  month: +month,
  year: +year,
});
};

// Map and filter out null values, if they exist
const formatDatesArray = (array: string[]) => {
const formattedArray = array
  .map((startDate: string) => parseDateString(startDate))
  .filter((date): date is Date => date !== null); // Type guard to ensure only valid Dates are kept;

return formattedArray;
};

const handleMonthChange = (data: { month: number; year: number }): void => {
  if (!currentMonth.value || !currentYear.value) return;
  if (isSelectingEndDateMode.value) return;

  // Convert `data.month` to a 2-digit string with leading zero
  const formattedMonth = data.month.toString().padStart(2, "0");
  const formattedYear = data.year.toString();

  if (
    currentMonth.value === formattedMonth &&
    currentYear.value === formattedYear
  )
    return;

  currentYear.value = formattedYear;
  currentMonth.value = formattedMonth;

  fetchStartDates(currentMonth.value);
};

const isFetchingDatesTemporarily = ref(false);
 
const handlePagesChange = (pages: { month: number; year: number }[]) => {
  const firstPage = pages[0]; // only handle left column for simplicity
  const formattedMonth = firstPage.month.toString().padStart(2, "0");
  const formattedYear = firstPage.year.toString();
 
  const isSameMonthYear =
    currentMonth.value === formattedMonth &&
    currentYear.value === formattedYear;
 
 if (!isSameMonthYear && !isFetchingDatesTemporarily.value && !isSelectingEndDateMode.value) {
    currentMonth.value = formattedMonth;
    currentYear.value = formattedYear;
 
    isFetchingDatesTemporarily.value = true;
 
    // Debounce by locking 500ms
    setTimeout(() => {
      isFetchingDatesTemporarily.value = false;
    }, 500);
 
    fetchStartDates(currentMonth.value);
  }
};

const fetchStartDates = async (
monthToFetch: string,
isHandleSearch?: boolean
): Promise<void | null> => {
if (!currentYear.value || !monthToFetch) return null;

const nextMonth: string = getAdjacentMonth(monthToFetch, "next");
const prevMonth: string = getAdjacentMonth(monthToFetch, "prev");

const monthsToFetch: string[] = [prevMonth, monthToFetch, nextMonth]

try {
  // Fetch start dates for each month
  const startDatesPromises: Promise<string[]>[] = monthsToFetch.map(
    (month) => {
      startDatesParams.value.month = `${currentYear.value}-${month}`;
      return window.searchEngineService.getStartDates({
        ...generalParams.value,
        ...startDatesParams.value,
      });
    }
  );

  // Resolve all start dates
  const resolvedStartDates: string[][] = await Promise.all(
    startDatesPromises
  );

  // Format and collect all start dates
  const allStartDates = resolvedStartDates.flatMap((startDates) =>
    formatDatesArray(startDates)
  );

  // Remove duplicates and update availableDates
  availableDates.value = isHandleSearch ? 
  [
    ...new Set([...allStartDates]),
  ] :
  [
    ...new Set([...availableDates.value, ...allStartDates]),
  ];
} catch (error) {
  console.error("Error fetching start dates at fetchStartDates:", error);
}
};


const generateDisabledDates = (available: Date[]): Date[] => {
  const result: Date[] = [];
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  startDate.setFullYear(today.getFullYear() - 5);
  endDate.setFullYear(today.getFullYear() + 3);

  const availableSet = new Set(available.map(date => format(date, 'yyyy-MM-dd')));

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dayStr = format(d, 'yyyy-MM-dd');
    if (!availableSet.has(dayStr)) {
      result.push(new Date(d));
    }
  }

  return result;
};

const disabledDates = computed(() => generateDisabledDates(availableDates.value));
/* handle initial data */
const preFill = async () => {
  if (!startDate.value || !endDate.value) return;
  if (!attributesRef.value[0].dates) return;

  // Set initial data
  attributesRef.value[0].dates.start = startDate.value;
  attributesRef.value[0].dates.end = endDate.value;

  range.value.start = store.state.dates["BeginDate"];
  range.value.end = store.state.dates["EndDate"];

  const beginDateFromStore: string  = store.state.dates["BeginDate"];
  const monthFromBeginDate: string = beginDateFromStore.split("/")[1];
  const yearFromBeginDate: string  = beginDateFromStore.split("/")[2];

  currentMonth.value = monthFromBeginDate;
  currentYear.value = yearFromBeginDate;

  // Fetch start dates
  fetchStartDates(currentMonth.value);

  // Movo calendar to relevant date
  moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
  };

  onMounted(() => {
    // console.log("[DEBUG] Picker mode default:", datePickerRef.value?.mode);
    onClickOutside(datePickerContainerRef, () => toggleShowHideDatePicker(false))
  })

// Watches
// Watch for changes in store.state.packCode and reset the search
watch(
  () => store.state.packCode, // Reactive source
  (newPackCode) => {
    // Parse the hash parameters
    const hashParams = parseHashParams() as HashParams;

    // Update params with new values for packCode and season
    generalParams.value.packCode = newPackCode;
    availabilitiesParams.value.season = hashParams.season;

    // Close the DatePicker, if it is open
    toggleShowHideDatePicker(false);

    // Prepare date
    const beginDateFromStore = store.state.dates["BeginDate"];
    const monthFromBeginDate = beginDateFromStore.split("/")[1];
    const yearFromBeginDate = beginDateFromStore.split("/")[2];

    // Fetch start dates
    fetchStartDates(currentMonth.value);

    // Movo calendar to relevant date
    moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
  }
);

watch(
  () => store.state.participants, // Reactive source
  (newBeginDate) => {
    // Close the DatePicker, if it is open
    toggleShowHideDatePicker(false);
  }
);

// Watch for changes in store.state.dates["BeginDate"] and update startDate
watch(
  () => store.state.dates["BeginDate"], // Reactive source
  (newBeginDate) => {
    startDate.value = parseDateString(newBeginDate); // Update startDate
  }
);

// Watch for changes in store.state.dates["EndDate"] and update endDate
watch(
  () => store.state.dates["EndDate"], // Reactive source
  (newEndDate) => {
    endDate.value = parseDateString(newEndDate); // Update endDate
  }
);


watch(
  () => store.state.loading, // Reactive source
  (newEndDate) => {
    preFill();
  }
);

watch(availableDates, (val) => {
  // console.log("Available Dates Ã¢â€ â€™", val.map(d => d.toDateString()));
  // console.log("Valid Dates?", val.every(d => d instanceof Date));
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      id: "datePickerDiv",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (toggleShowHideDatePicker()))
    }, [
      _createElementVNode("div", _hoisted_1, [
        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "me-2 align-self-center" }, [
          _createElementVNode("svg", { class: "icon tf-icon-22 text-blue-900" }, [
            _createElementVNode("use", { "xlink:href": "#___icon___calander" })
          ])
        ], -1)),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("label", _hoisted_3, _toDisplayString(range.value.start && range.value.end
              ? `${range.value.start} - ${range.value.end}`
              : content.value.planner_datePicker_depart_return), 1)
        ])
      ])
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["datePickerContainerRef datepicker-input", [isOpenedRef.value ? 'show' : 'hide']]),
      ref_key: "datePickerContainerRef",
      ref: datePickerContainerRef
    }, [
      _createVNode(_unref(VDatePicker), {
        modelValue: selectedDate.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedDate).value = $event)),
        locale: locale.value,
        ref_key: "datePickerRef",
        ref: datePickerRef,
        columns: 2,
        rows: 1,
        color: selectedColor.value,
        attributes: attributesRef.value,
        "available-dates": availableDates.value,
        mode: "date",
        "disabled-dates": disabledDates.value,
        onDayclick: selectDate,
        "onUpdate:pages": handlePagesChange
      }, {
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("label", _hoisted_5, _toDisplayString(range.value.start
                ? `${departureText.value} - ${returnText.value}`
                : content.value.planner_datePicker_depart_return), 1)
          ])
        ]),
        _: 1
      }, 8, ["modelValue", "locale", "color", "attributes", "available-dates", "disabled-dates"])
    ], 2)
  ], 64))
}
}

})